import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from "react";
import { useDeferHeavyProvidersForCurrentRoute } from "@/lib/routeScope";
import * as productRegistryService from "@/services/productRegistry.service";

export type ProductCategory =
  | "Wall System"
  | "Base"
  | "Glass/Enclosure"
  | "Hardware"
  | "Add-On"
  | "Miscellaneous"
  | "Other";

export interface RegistryProduct {
  productId: string;
  productName: string;
  sku: string;
  vendor: string;
  category: ProductCategory;
  subcategory: string;
  unitOfMeasure: string;
  cost: number;
  price: number;
  trackInventory: boolean;
  stockQuantity: number;
  reorderThreshold: number;
  active: boolean;
  notes: string;
}

export interface ProductRegistryContextValue {
  products: RegistryProduct[];
  setProducts: React.Dispatch<React.SetStateAction<RegistryProduct[]>>;
  getProductBySku: (sku: string) => RegistryProduct | undefined;
  getProductsByCategory: (cat: ProductCategory) => RegistryProduct[];
  getActiveProducts: () => RegistryProduct[];
  getProductsByVendor: (vendor: string) => RegistryProduct[];
  allVendors: string[];
  isLoading: boolean;
  error: string | null;
  refreshProducts: () => Promise<void>;
}

const ProductRegistryContext = createContext<ProductRegistryContextValue | null>(null);

export function ProductRegistryProvider({ children }: { children: ReactNode }) {
  const [products, setProductsState] = useState<RegistryProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const deferHeavyProvider = useDeferHeavyProvidersForCurrentRoute();

  const refreshProducts = useCallback(async () => {
    if (deferHeavyProvider) {
      setIsLoading(false);
      setError(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await productRegistryService.getProducts();
      setProductsState(data);
    } catch (err) {
      console.error("Failed to fetch products from API:", err);
      setProductsState([]);
      setError("Failed to load products from the database.");
    } finally {
      setIsLoading(false);
    }
  }, [deferHeavyProvider]);

  useEffect(() => {
    if (deferHeavyProvider) {
      setIsLoading(false);
      return;
    }
    refreshProducts();
  }, [deferHeavyProvider, refreshProducts]);

  const getProductBySku = useCallback(
    (sku: string) => products.find((p) => p.sku === sku),
    [products],
  );

  const getProductsByCategory = useCallback(
    (cat: ProductCategory) => products.filter((p) => p.category === cat && p.active),
    [products],
  );

  const getActiveProducts = useCallback(() => products.filter((p) => p.active), [products]);

  const getProductsByVendor = useCallback(
    (vendor: string) => products.filter((p) => p.vendor === vendor && p.active),
    [products],
  );

  const allVendors = useMemo(
    () => [...new Set(products.map((p) => p.vendor).filter(Boolean))].sort(),
    [products],
  );

  return (
    <ProductRegistryContext.Provider
      value={{
        products,
        setProducts: setProductsState,
        getProductBySku,
        getProductsByCategory,
        getActiveProducts,
        getProductsByVendor,
        allVendors,
        isLoading,
        error,
        refreshProducts,
      }}
    >
      {children}
    </ProductRegistryContext.Provider>
  );
}

export function useProductRegistry() {
  const ctx = useContext(ProductRegistryContext);
  if (!ctx) throw new Error("useProductRegistry must be used within ProductRegistryProvider");
  return ctx;
}

export const PRODUCT_CATEGORIES: ProductCategory[] = [
  "Wall System", "Base", "Glass/Enclosure", "Hardware", "Add-On", "Miscellaneous", "Other",
];
